import { useSAuthState } from '~/composables/auth'
import { useJoinStore } from '~/stores/join'

interface AgentJoinEntryDeps {
  // The page's sponsor-link and select-offer steps, shared with its guest entries.
  applySponsorFromLink: () => void
  advanceToSelectOffer: (options?: { replace?: boolean }) => void
}

// How an agent enters the join: as a throwaway test recruit, never through the guest entry
// pop-up. Kept out of the join page so it holds only view state and the template. Call from
// setup: it registers its own unmount hook. AB#64477
export const useAgentJoinEntry = ({ applySponsorFromLink, advanceToSelectOffer }: AgentJoinEntryDeps) => {
  const joinStore = useJoinStore()
  const { isAgent, isAgentRecruit } = useSAuthState()

  // Set when the agent navigates away (Back, a link) while join/agent-recruit is still pending.
  let leftPage = false
  onBeforeUnmount(() => {
    leftPage = true
  })

  // Starts the test recruit (also the retry after a failed start). On failure the store error
  // shows on the page.
  const beginAgentJoin = async () => {
    applySponsorFromLink()
    const started = await joinStore.startAsAgent()
    // The agent left while the recruit was being created. A success has by now set the isRecruit
    // cookie, the impersonation context and the Recruit role, so redirecting would pull them back
    // into the join from wherever they went: leave the recruit instead. One retry, since there is
    // no page left to show an error on; if that fails too the recruit cookie stays set, so
    // recruit-guard returns the agent to /join on their next navigation, where the page resumes
    // the test recruit and JoinHeader's Exit shows the error.
    if (leftPage) {
      if (started) {
        const left = await joinStore.leaveAsAgent()
        if (!left) {
          await joinStore.leaveAsAgent()
        }
      }
      return
    }
    if (started) {
      advanceToSelectOffer()
    }
  }

  // The landing page is not under the join layout, so on a full load nothing has hydrated the
  // join store: fetch the recruit record here before resuming. If it cannot be loaded, the error
  // shows with no retry (a recruit token cannot start another test recruit) and JoinHeader's Exit
  // returns the agent to their own session -- never the guest entry pop-up, which would run
  // check-email and registration while the agent is impersonating.
  const resumeAgentJoin = async () => {
    applySponsorFromLink()
    if (!joinStore.session.email) {
      await joinStore.hydrateSession()
    }
    if (joinStore.session.email) {
      advanceToSelectOffer({ replace: true })
    }
  }

  // Returns whether the visitor entered as an agent; the caller then shows the agent view.
  const enterAsAgent = (): boolean => {
    // An agent starts straight away as a test recruit: no entry pop-up, check-email,
    // registration or welcome-back.
    if (isAgent.value) {
      beginAgentJoin()
      return true
    }
    // A test recruit already under way, reaching /join through a full load (a second tab, or
    // recruit-guard sending a hard-loaded URL here). Its role reads Recruit/Anonymous, not Agent.
    if (isAgentRecruit.value) {
      resumeAgentJoin()
      return true
    }
    return false
  }

  return { enterAsAgent, beginAgentJoin }
}
